/**
 * NewsletterVerify.js
 * Handles the double opt-in landing when user clicks the email verification link.
 * URL: /newsletter/verify?status=verified|already_done|invalid&token=X
 *
 * status=verified     → token just activated; show onboarding questions then success
 * status=already_done → was already verified; show success with referral link
 * status=invalid      → token invalid/expired; show error state
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Container, Button, CircularProgress, Alert,
    Stack, LinearProgress, Chip, IconButton, Tooltip,
} from '@mui/material';
import {
    CheckCircle as CheckIcon,
    Error as ErrorIcon,
    ContentCopy as CopyIcon,
    Twitter as TwitterIcon,
    WhatsApp as WhatsAppIcon,
    Instagram as InstagramIcon,
    YouTube as YouTubeIcon,
    Facebook as FacebookIcon,
    AutoAwesome as SparkleIcon,
    Group as GroupIcon,
    TrendingUp as TrendingIcon,
    Lightbulb as IdeaIcon,
    Schedule as ScheduleIcon,
    Analytics as AnalyticsIcon,
    Forum as EngageIcon,
    ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { newsletterApi } from '../services/api';

// ── Shared constants ──────────────────────────────────────────────────────────

const ROLES = [
    { id: 'agency',          label: 'Agency Owner',         icon: '🏢', desc: 'Managing multiple client accounts' },
    { id: 'social_manager',  label: 'Social Media Manager', icon: '📱', desc: 'Running brand social channels' },
    { id: 'business_owner',  label: 'Business Owner',       icon: '💼', desc: 'Growing my own brand' },
    { id: 'content_creator', label: 'Content Creator',      icon: '🎨', desc: 'Building my personal audience' },
];

const PLATFORMS = [
    { id: 'instagram', label: 'Instagram', icon: <InstagramIcon sx={{ fontSize: 18 }} /> },
    { id: 'youtube',   label: 'YouTube',   icon: <YouTubeIcon   sx={{ fontSize: 18 }} /> },
    { id: 'facebook',  label: 'Facebook',  icon: <FacebookIcon  sx={{ fontSize: 18 }} /> },
    { id: 'tiktok',    label: 'TikTok',    icon: <SparkleIcon   sx={{ fontSize: 18 }} /> },
    { id: 'linkedin',  label: 'LinkedIn',  icon: <GroupIcon     sx={{ fontSize: 18 }} /> },
    { id: 'twitter',   label: 'Twitter/X', icon: <TwitterIcon   sx={{ fontSize: 18 }} /> },
];

const PAIN_POINTS = [
    { id: 'ideas',      label: 'Coming up with ideas',    icon: <IdeaIcon      sx={{ fontSize: 20 }} /> },
    { id: 'scheduling', label: 'Consistent scheduling',   icon: <ScheduleIcon  sx={{ fontSize: 20 }} /> },
    { id: 'analytics',  label: 'Understanding analytics', icon: <AnalyticsIcon sx={{ fontSize: 20 }} /> },
    { id: 'engagement', label: 'Growing engagement',      icon: <EngageIcon    sx={{ fontSize: 20 }} /> },
    { id: 'all',        label: 'All of the above 😅',     icon: <TrendingIcon  sx={{ fontSize: 20 }} /> },
];

// ── Sub-components ────────────────────────────────────────────────────────────

function SelectCard({ selected, onClick, children }) {
    return (
        <Box onClick={onClick} sx={{
            p: 2, borderRadius: '12px', cursor: 'pointer',
            border: `1.5px solid ${selected ? '#6366f1' : 'rgba(255,255,255,0.08)'}`,
            bgcolor: selected ? 'rgba(99,102,241,0.12)' : 'rgba(255,255,255,0.02)',
            transition: 'all 0.2s',
            '&:hover': { bgcolor: 'rgba(99,102,241,0.07)', borderColor: 'rgba(99,102,241,0.4)' },
        }}>
            {children}
        </Box>
    );
}

// ── Onboarding questions (same as NewsletterSection step 2) ───────────────────

function OnboardingStep({ token, email, onDone }) {
    const [role,      setRole]      = useState('');
    const [platforms, setPlatforms] = useState([]);
    const [painPoint, setPainPoint] = useState('');
    const [subStep,   setSubStep]   = useState(0);
    const [loading,   setLoading]   = useState(false);
    const [error,     setError]     = useState(null);

    const togglePlatform = id =>
        setPlatforms(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);

    const handleNext = async () => {
        if (subStep === 0 && !role)             { setError('Please select your role.'); return; }
        if (subStep === 1 && !platforms.length) { setError('Select at least one platform.'); return; }
        if (subStep < 2)  { setError(null); setSubStep(s => s + 1); return; }
        if (!painPoint)   { setError('Please pick your biggest challenge.'); return; }

        setLoading(true); setError(null);
        try {
            const res = await newsletterApi.saveOnboarding(email, { role, platforms, painPoint });
            onDone(res);
        } catch {
            setError('Something went wrong saving your answers. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <LinearProgress
                variant="determinate"
                value={((subStep + 1) / 3) * 100}
                sx={{ mb: 4, height: 3, borderRadius: '100px', bgcolor: 'rgba(255,255,255,0.06)', '& .MuiLinearProgress-bar': { bgcolor: '#6366f1' } }}
            />

            {subStep === 0 && (
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>What best describes you?</Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', mb: 4 }}>This helps us personalise your experience.</Typography>
                    <Stack spacing={2}>
                        {ROLES.map(r => (
                            <SelectCard key={r.id} selected={role === r.id} onClick={() => setRole(r.id)}>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <Box sx={{ fontSize: '1.5rem' }}>{r.icon}</Box>
                                    <Box sx={{ textAlign: 'left' }}>
                                        <Typography sx={{ fontWeight: 700, color: 'white' }}>{r.label}</Typography>
                                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>{r.desc}</Typography>
                                    </Box>
                                </Stack>
                            </SelectCard>
                        ))}
                    </Stack>
                </Box>
            )}

            {subStep === 1 && (
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>Which platforms do you manage?</Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', mb: 4 }}>Select all that apply.</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
                        {PLATFORMS.map(p => (
                            <SelectCard key={p.id} selected={platforms.includes(p.id)} onClick={() => togglePlatform(p.id)}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 0.5 }}>
                                    <Box sx={{ color: platforms.includes(p.id) ? '#6366f1' : 'rgba(255,255,255,0.5)' }}>{p.icon}</Box>
                                    <Typography sx={{ fontWeight: 700, color: 'white', fontSize: '0.9rem' }}>{p.label}</Typography>
                                </Stack>
                            </SelectCard>
                        ))}
                    </Box>
                </Box>
            )}

            {subStep === 2 && (
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>What's your biggest challenge?</Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', mb: 4 }}>We'll build features around what matters most to you.</Typography>
                    <Stack spacing={2}>
                        {PAIN_POINTS.map(p => (
                            <SelectCard key={p.id} selected={painPoint === p.id} onClick={() => setPainPoint(p.id)}>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <Box sx={{ color: painPoint === p.id ? '#6366f1' : 'rgba(255,255,255,0.4)' }}>{p.icon}</Box>
                                    <Typography sx={{ fontWeight: 700, color: 'white', textAlign: 'left' }}>{p.label}</Typography>
                                </Stack>
                            </SelectCard>
                        ))}
                    </Stack>
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mt: 3, borderRadius: '12px', bgcolor: 'rgba(244,67,54,0.08)', color: '#ff8a80' }}>
                    {error}
                </Alert>
            )}

            <Button
                onClick={handleNext}
                disabled={loading}
                variant="contained"
                fullWidth
                endIcon={!loading && (subStep < 2 ? <ArrowIcon /> : <CheckIcon />)}
                sx={{
                    mt: 4, height: 52, borderRadius: '100px',
                    bgcolor: '#6366f1', fontWeight: 800, fontSize: '1rem',
                    '&:hover': { bgcolor: '#818cf8' },
                }}
            >
                {loading ? <CircularProgress size={20} color="inherit" /> : subStep < 2 ? 'Next' : 'Finish — Secure My Spot'}
            </Button>
        </Box>
    );
}

// ── Success card ──────────────────────────────────────────────────────────────

function SuccessCard({ email, refLink, queuePosition, segment }) {
    const [copied, setCopied] = useState(false);
    const shareUrl  = refLink || window.location.origin;
    const shareText = encodeURIComponent(
        `I just joined the Socialixer waitlist — AI social media automation. Join me: ${shareUrl}?utm_source=twitter&utm_medium=social&utm_campaign=waitlist_referral`
    );

    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(shareUrl)
            .then(() => { setCopied(true); setTimeout(() => setCopied(false), 2500); })
            .catch(() => {});
    }, [shareUrl]);

    return (
        <Box>
            <Typography sx={{ fontSize: '3rem', mb: 2 }}>🎉</Typography>
            <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.03em', mb: 2 }}>You're in!</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.55)', mb: 4 }}>
                {email && <><strong style={{ color: 'white' }}>{email}</strong> — your spot is confirmed.</>}
            </Typography>

            {queuePosition && (
                <Box sx={{ p: 3, mb: 4, borderRadius: '16px', background: 'linear-gradient(135deg, rgba(99,102,241,0.15), rgba(168,85,247,0.1))', border: '1px solid rgba(99,102,241,0.25)' }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 700, letterSpacing: '0.12em' }}>YOUR QUEUE POSITION</Typography>
                    <Typography variant="h2" sx={{ fontWeight: 900, color: '#6366f1', fontSize: '3rem' }}>#{queuePosition}</Typography>
                    {segment && segment !== 'General' && (
                        <Chip label={segment} size="small" sx={{ mt: 1, bgcolor: 'rgba(99,102,241,0.2)', color: '#a5b4fc', fontWeight: 700 }} />
                    )}
                </Box>
            )}

            {refLink && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'rgba(255,255,255,0.7)' }}>
                        🔗 Share to move up the queue
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        <Box sx={{ flex: 1, p: 1.5, px: 2, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden' }}>
                            <Typography noWrap sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', fontFamily: 'monospace' }}>{shareUrl}</Typography>
                        </Box>
                        <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
                            <IconButton onClick={handleCopy} aria-label="Copy referral link" sx={{ bgcolor: copied ? 'rgba(16,217,138,0.15)' : 'rgba(99,102,241,0.15)', color: copied ? '#10d98a' : '#a5b4fc', borderRadius: '12px' }}>
                                {copied ? <CheckIcon /> : <CopyIcon />}
                            </IconButton>
                        </Tooltip>
                    </Stack>
                    <Stack direction="row" spacing={1.5} sx={{ mt: 2, justifyContent: 'center' }}>
                        <Button href={`https://twitter.com/intent/tweet?text=${shareText}`} target="_blank" rel="noopener noreferrer" component="a" startIcon={<TwitterIcon />} size="small"
                            sx={{ borderRadius: '100px', color: 'white', fontWeight: 700, textTransform: 'none', bgcolor: 'rgba(29,161,242,0.15)', border: '1px solid rgba(29,161,242,0.25)' }}>
                            Share on X
                        </Button>
                        <Button href={`https://wa.me/?text=${encodeURIComponent(`Join me on Socialixer waitlist: ${shareUrl}?utm_source=whatsapp&utm_medium=social&utm_campaign=waitlist_referral`)}`} target="_blank" rel="noopener noreferrer" component="a" startIcon={<WhatsAppIcon />} size="small"
                            sx={{ borderRadius: '100px', color: 'white', fontWeight: 700, textTransform: 'none', bgcolor: 'rgba(37,211,102,0.12)', border: '1px solid rgba(37,211,102,0.2)' }}>
                            Share on WhatsApp
                        </Button>
                    </Stack>
                </Box>
            )}

            <Button href="/" sx={{ color: 'rgba(255,255,255,0.4)', textTransform: 'none', '&:hover': { color: 'rgba(255,255,255,0.7)' } }}>
                ← Back to Socialixer
            </Button>
        </Box>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function NewsletterVerify() {
    const params  = new URLSearchParams(window.location.search);
    const status  = params.get('status');   // verified | already_done | invalid
    const token   = params.get('token');

    // 'onboarding' → showing questions; 'success' → showing success card
    const [view,     setView]     = useState(status === 'invalid' ? 'invalid' : status === 'already_done' ? 'success' : 'onboarding');
    const [prefData, setPrefData] = useState(null);
    const [loading,  setLoading]  = useState(false);

    // Fetch existing subscriber data to pre-fill onboarding if available
    useEffect(() => {
        if (!token || status === 'invalid') return;
        setLoading(true);
        newsletterApi.getPreferences(token)
            .then(data => {
                setPrefData(data);
                // If onboarding already done jump straight to success
                if (data.role) setView('success');
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [token, status]);

    const handleOnboardingDone = (res) => {
        setPrefData(prev => ({ ...prev, ...res }));
        setView('success');
    };

    const cardStyle = {
        p: { xs: 4, md: 6 }, borderRadius: '24px',
        bgcolor: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.07)',
        backdropFilter: 'blur(16px)', textAlign: 'center',
        boxShadow: '0 40px 100px rgba(0,0,0,0.4)',
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#0a0a14', py: { xs: 6, md: 12 } }}>
            <Container maxWidth="sm">
                {loading ? (
                    <Box sx={{ ...cardStyle, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                        <CircularProgress sx={{ color: '#6366f1' }} />
                        <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>Loading your details…</Typography>
                    </Box>
                ) : view === 'invalid' ? (
                    <Box sx={cardStyle}>
                        <ErrorIcon sx={{ fontSize: 56, color: '#ef4444', mb: 2 }} />
                        <Typography variant="h4" sx={{ fontWeight: 900, mb: 2 }}>Invalid link</Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.5)', mb: 4 }}>
                            This verification link has expired or already been used.
                        </Typography>
                        <Button href="/" variant="contained" sx={{ bgcolor: '#6366f1', borderRadius: '100px', fontWeight: 700, '&:hover': { bgcolor: '#818cf8' } }}>
                            Back to homepage
                        </Button>
                    </Box>
                ) : view === 'onboarding' ? (
                    <Box sx={cardStyle}>
                        <Box sx={{ mb: 4 }}>
                            <CheckIcon sx={{ fontSize: 48, color: '#10d98a', mb: 2 }} />
                            <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>Email verified ✓</Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                Tell us a bit about yourself to personalise your experience.
                            </Typography>
                        </Box>
                        <OnboardingStep
                            token={token}
                            email={prefData?.email || ''}
                            onDone={handleOnboardingDone}
                        />
                    </Box>
                ) : (
                    <Box sx={cardStyle}>
                        <SuccessCard
                            email={prefData?.email}
                            refLink={prefData?.refLink}
                            queuePosition={prefData?.queuePosition}
                            segment={prefData?.segment}
                        />
                    </Box>
                )}
            </Container>
        </Box>
    );
}
